import type { MapDef } from '../world/map';
import { fbm, hash2, hex, mix, noise2, scale, smoothstep, type RGB } from './noise';

/**
 * Ground materials of the cks package. Tile chars reserved for it: '7', '8' walkable, '9' solid
 * (solidity is declared in src/world/tiles.ts). Runs inside the ground worker too,
 * so keep it pure: no DOM, no game state. Return null for chars you don't own.
 *
 * Liberty Square ('cks'): 7 = the great granite square, 8 = red-brick garden paths, 9 = koi ponds.
 * Beitou ('beitou'): 7 = mossy flagstone paths, 8 = wooden boardwalks, 9 = steaming hot-spring water.
 */
const T = 16;
const tile = (map: MapDef, tx: number, ty: number): string => map.rows[ty]?.[tx] ?? ' ';

/** Distance (px) to the nearest tile that isn't `self`, looking two tiles out. */
function edgeDist(map: MapDef, x: number, y: number, self: string): number {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  const other = (dx: number, dy: number) => tile(map, tx + dx, ty + dy) !== self;
  let d = 40;
  if (other(-1, 0)) d = Math.min(d, fx);
  if (other(1, 0)) d = Math.min(d, T - fx);
  if (other(0, -1)) d = Math.min(d, fy);
  if (other(0, 1)) d = Math.min(d, T - fy);
  if (other(-2, 0)) d = Math.min(d, fx + T);
  if (other(2, 0)) d = Math.min(d, 2 * T - fx);
  if (other(0, -2)) d = Math.min(d, fy + T);
  if (other(0, 2)) d = Math.min(d, 2 * T - fy);
  if (other(-1, -1)) d = Math.min(d, Math.hypot(fx, fy));
  if (other(1, -1)) d = Math.min(d, Math.hypot(T - fx, fy));
  if (other(-1, 1)) d = Math.min(d, Math.hypot(fx, T - fy));
  if (other(1, 1)) d = Math.min(d, Math.hypot(T - fx, T - fy));
  return d;
}

// --- Liberty Square --------------------------------------------------------------------------

/** A fallen leaf (ochre / brown) lying in this 10 px cell, if any: returns its colour or null. */
function leafAt(x: number, y: number, seed: number, rate = 0.035): RGB | null {
  const S = 10;
  const cx = Math.floor(x / S), cy = Math.floor(y / S);
  const h = hash2(cx, cy, seed);
  if (h > rate) return null;
  const lx = (cx + 0.2 + hash2(cx, cy, seed + 1) * 0.6) * S, ly = (cy + 0.2 + hash2(cx, cy, seed + 2) * 0.6) * S;
  const a = hash2(cx, cy, seed + 3) * Math.PI;
  const dx = x - lx, dy = y - ly;
  const u = dx * Math.cos(a) + dy * Math.sin(a), v = -dx * Math.sin(a) + dy * Math.cos(a);
  if ((u * u) / 7.5 + (v * v) / 1.8 > 1) return null;
  const k = h / rate;
  return mix(hex('#b8862a'), k < 0.33 ? hex('#8a5a2a') : k < 0.66 ? hex('#d0b060') : hex('#a0642a'), 0.6);
}

/**
 * The great square: big pale granite slabs in a grid (three tones, chamfered joints), a grand grid of
 * rose-granite bands every 8 slabs with dark corner nodes and a diamond motif, drain grates, worn
 * patches where people walk, lichen in the joints and the odd fallen leaf.
 */
function squareSlabs(x: number, y: number): RGB {
  const S = 24;
  const cx = Math.floor(x / S), cy = Math.floor(y / S);
  const u = x - cx * S, v = y - cy * S;
  const tone = hash2(cx, cy, 701);
  let c = tone < 0.33 ? hex('#d8d3c8') : tone < 0.66 ? hex('#cdc7ba') : hex('#c2bcb0');
  if (tone > 0.97) c = hex('#b4aea4'); // a replaced slab, darker
  const bx = ((cx % 8) + 8) % 8, by = ((cy % 8) + 8) % 8;
  const band = bx === 0 || by === 0;
  if (band) c = mix(hex('#b9a49a'), hex('#c8b2a6'), tone);
  if (bx === 0 && by === 0) c = hex('#8e7a72'); // corner node
  // Diamond motif in the middle of every grand square (rose outline).
  if (!band) {
    const mx = Math.abs(bx + u / S - 4), my = Math.abs(by + v / S - 4);
    const dd = Math.abs(mx + my - 2.4);
    if (dd < 0.07) c = mix(c, hex('#b08a80'), 0.75);
  }
  // Drain grate on the band, one per grand square.
  if (by === 0 && bx === 4 && u > 4 && u < S - 4 && v > 7 && v < S - 7) {
    c = Math.floor(u / 2.6) % 2 === 0 ? hex('#4a4a46') : hex('#6c6a64');
  }
  c = scale(c, 0.97 + noise2(x * 0.35, y * 0.35, 702) * 0.06);
  if (hash2(Math.floor(x * 1.5), Math.floor(y * 1.5), 703) < 0.05) c = scale(c, 0.92);
  // Joints: a dark line on the top/left, a light lip on the bottom/right; lichen creeping into some.
  if (u < 0.7 || v < 0.7) {
    c = scale(c, 0.78);
    if (noise2(x * 0.08, y * 0.08, 706) > 0.68) c = mix(c, hex('#6f8a52'), 0.5);
  } else if (u > S - 1.2 || v > S - 1.2) c = scale(c, 1.05);
  // Worn, slightly darker patches and a gentle sheen from years of footsteps.
  c = mix(c, hex('#a8a096'), smoothstep(0.62, 0.8, fbm(x / 90, y / 45, 705)) * 0.22);
  c = mix(c, hex('#f4f1ea'), smoothstep(0.6, 0.78, fbm(x / 60, y / 30, 704)) * 0.18);
  const leaf = leafAt(x, y, 707, 0.02);
  if (leaf) return leaf;
  return c;
}

/** Red-brick garden paths (running bond). */
function brickPath(x: number, y: number): RGB {
  const bh = 4, bw = 9;
  const row = Math.floor(y / bh);
  const off = row % 2 ? bw / 2 : 0;
  const col = Math.floor((x + off) / bw);
  const u = x + off - col * bw, v = y - row * bh;
  const tone = hash2(col, row, 711);
  let c = mix(hex('#b98270'), hex('#c8937e'), tone);
  if (tone > 0.92) c = hex('#a8705e');
  c = scale(c, 0.96 + noise2(x * 0.6, y * 0.6, 712) * 0.08);
  if (u < 0.7 || v < 0.7) c = noise2(x * 0.1, y * 0.1, 713) > 0.62 ? hex('#6f8a52') : hex('#a87a68');
  // Damp, mossy patches under the trees and the odd fallen leaf.
  c = mix(c, hex('#7a8a5a'), smoothstep(0.66, 0.84, fbm(x / 40, y / 40, 714)) * 0.35);
  const leaf = leafAt(x, y, 715, 0.045);
  if (leaf) return leaf;
  return c;
}

/** Koi pond: deep green water with a stone rim, lily pads and a lighter shallow edge. */
function koiPond(map: MapDef, x: number, y: number): RGB {
  const d = edgeDist(map, x, y, '9') + (noise2(x * 0.25, y * 0.25, 721) - 0.5) * 1.2;
  if (d < 2.2) return d < 1 ? hex('#8a8a84') : hex('#b8b6ae');
  let c = mix(hex('#4f8f86'), hex('#1f4f56'), smoothstep(2, 26, d));
  c = mix(c, hex('#8ac8c0'), smoothstep(0.66, 0.8, fbm(x / 26, y / 12, 722)) * 0.25);
  // Lily pads (with a notch) and the odd pink flower.
  const S = 14;
  const cx = Math.floor(x / S), cy = Math.floor(y / S);
  const h = hash2(cx, cy, 723);
  if (h < 0.22 && d > 6) {
    const px = (cx + 0.3 + hash2(cx, cy, 724) * 0.4) * S, py = (cy + 0.3 + hash2(cx, cy, 725) * 0.4) * S;
    const rr = 2.6 + h * 8;
    const dx = x - px, dy = (y - py) * 1.4;
    const dist = Math.hypot(dx, dy);
    const notch = dx > 0 && Math.abs(dy) < 0.6;
    if (dist < rr && !notch) {
      c = mix(hex('#3f8a3a'), hex('#6fb05a'), dist / rr);
      if (h < 0.05 && dist < 1.3) c = hex('#f08ab0');
    }
  }
  return c;
}

// --- Beitou ------------------------------------------------------------------------------------

/** Mossy grey flagstones (park paths). */
function flagstones(x: number, y: number): RGB {
  const S = 7;
  const cx = Math.floor(x / S + noise2(x * 0.1, y * 0.1, 731) * 0.6), cy = Math.floor(y / S + noise2(x * 0.1, y * 0.1, 732) * 0.6);
  const tone = hash2(cx, cy, 733);
  let c = mix(hex('#8e8c84'), hex('#b2ada0'), tone);
  const fx = x / S + noise2(x * 0.1, y * 0.1, 731) * 0.6 - cx, fy = y / S + noise2(x * 0.1, y * 0.1, 732) * 0.6 - cy;
  if (fx < 0.09 || fy < 0.09) c = mix(hex('#4f6a3a'), hex('#5f7a44'), hash2(Math.floor(x), Math.floor(y), 734));
  c = mix(c, hex('#6f8a52'), smoothstep(0.62, 0.8, fbm(x / 20, y / 20, 735)) * 0.4);
  return scale(c, 0.96 + noise2(x * 0.5, y * 0.5, 736) * 0.08);
}

/** Wooden boardwalk: brown planks with gaps and nail heads. */
function boardwalk(x: number, y: number): RGB {
  const ph = 4;
  const row = Math.floor(y / ph);
  const v = y - row * ph;
  const seg = Math.floor((x + hash2(row, 0, 741) * 40) / 40);
  const tone = hash2(seg, row, 742);
  let c = mix(hex('#8a5f3c'), hex('#a8774c'), tone);
  c = scale(c, 0.94 + fbm(x / 6, y / 1.5, 743) * 0.14);
  if (v < 0.7) c = hex('#3a2818');
  const u = ((x + hash2(row, 0, 741) * 40) % 40 + 40) % 40;
  if (u < 0.6) c = scale(c, 0.6);
  if ((u > 2 && u < 3 || u > 37 && u < 38) && v > 1.5 && v < 2.5) c = hex('#5a4a3a');
  return c;
}

/** Thermal Valley's jade-green, sulphurous water (top-right corner of the map). */
const inValley = (x: number, y: number) => x >= 56 * T && y <= 14 * T;

/** Hot-spring water: jade and milky in Thermal Valley, a cloudy warm teal in the stream and pools. */
function hotWater(map: MapDef, x: number, y: number): RGB {
  const d = edgeDist(map, x, y, '9') + (noise2(x * 0.3, y * 0.3, 751) - 0.5) * 1.6;
  const valley = inValley(x, y);
  if (d < 2.6) {
    // Wet rocks with yellow sulphur crust right at the water's edge.
    if (d < 1.2) return hex(hash2(Math.floor(x), Math.floor(y), 752) < 0.3 ? '#5a5650' : '#76716a');
    return valley ? mix(hex('#d8c460'), hex('#e8dc90'), hash2(Math.floor(x * 2), Math.floor(y * 2), 753)) : hex('#a8a89a');
  }
  let c = valley ? mix(hex('#a8e0c0'), hex('#4fb898'), smoothstep(3, 30, d)) : mix(hex('#a6c8b4'), hex('#5a9a8e'), smoothstep(3, 18, d));
  // Milky swirls; in the stream they stretch along the flow (down-left).
  const sw = valley ? fbm(x / 22, y / 16, 754) : fbm((x + y) / 12, (y - x) / 34, 755);
  c = mix(c, hex('#eef6e8'), smoothstep(0.58, 0.82, sw) * (valley ? 0.5 : 0.35));
  if (!valley && d < 5) c = mix(c, hex('#e8f0e4'), 0.3);
  return c;
}

export function colorAtCks(map: MapDef, x: number, y: number, mat: string): RGB | null {
  if (mat !== '7' && mat !== '8' && mat !== '9') return null;
  if (map.id === 'beitou') return mat === '7' ? flagstones(x, y) : mat === '8' ? boardwalk(x, y) : hotWater(map, x, y);
  return mat === '7' ? squareSlabs(x, y) : mat === '8' ? brickPath(x, y) : koiPond(map, x, y);
}

/** Flat colours shown while the detailed ground is still rendering. */
export const PLACEHOLDER_CKS: Record<string, string> = { '7': '#d2ccc0', '8': '#c08a76', '9': '#4f9a8a' };
